import React from 'react';

interface Competency {
  id: string;
  name: string;
  description: string;
  weight: number;
  activities: string[];
}

interface EvaluationDashboardResource {
  type: 'evaluation_dashboard';
  name: string;
  title: string;
  description: string;
  competencies: Competency[];
  showProgress: boolean;
  allowRetry: boolean;
}

interface EvaluationDashboardConfigurationFormProps {
  config: EvaluationDashboardResource;
  onChange: (config: EvaluationDashboardResource) => void;
}

export const EvaluationDashboardConfigurationForm: React.FC<EvaluationDashboardConfigurationFormProps> = ({
  config,
  onChange
}) => {
  const updateField = (field: keyof EvaluationDashboardResource, value: any) => {
    onChange({
      ...config,
      [field]: value
    });
  };

  const updateCompetency = (index: number, field: keyof Competency, value: any) => {
    const updatedCompetencies = [...config.competencies];
    updatedCompetencies[index] = {
      ...updatedCompetencies[index],
      [field]: value
    };
    updateField('competencies', updatedCompetencies);
  };

  const addCompetency = () => {
    const newCompetency: Competency = {
      id: `competency_${Date.now()}`,
      name: 'Nouvelle Compétence',
      description: 'Description de la compétence',
      weight: 10,
      activities: ['Activité 1']
    };
    updateField('competencies', [...config.competencies, newCompetency]);
  };

  const removeCompetency = (index: number) => {
    const updatedCompetencies = config.competencies.filter((_, i) => i !== index);
    updateField('competencies', updatedCompetencies);
  };

  const addActivity = (competencyIndex: number) => {
    const updatedCompetencies = [...config.competencies];
    updatedCompetencies[competencyIndex].activities.push('Nouvelle Activité');
    updateField('competencies', updatedCompetencies);
  };

  const removeActivity = (competencyIndex: number, activityIndex: number) => {
    const updatedCompetencies = [...config.competencies];
    updatedCompetencies[competencyIndex].activities = updatedCompetencies[competencyIndex].activities.filter((_, i) => i !== activityIndex);
    updateField('competencies', updatedCompetencies);
  };

  const updateActivity = (competencyIndex: number, activityIndex: number, value: string) => {
    const updatedCompetencies = [...config.competencies];
    updatedCompetencies[competencyIndex].activities[activityIndex] = value;
    updateField('competencies', updatedCompetencies);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nom du composant
        </label>
        <input
          type="text"
          value={config.name}
          onChange={(e) => updateField('name', e.target.value)}
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Titre affiché
        </label>
        <input
          type="text"
          value={config.title}
          onChange={(e) => updateField('title', e.target.value)}
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={config.description}
          onChange={(e) => updateField('description', e.target.value)}
          rows={3}
          className="input-field"
        />
      </div>

      <div className="flex space-x-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.showProgress}
            onChange={(e) => updateField('showProgress', e.target.checked)}
            className="mr-2"
          />
          Afficher la progression
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.allowRetry}
            onChange={(e) => updateField('allowRetry', e.target.checked)}
            className="mr-2"
          />
          Permettre de recommencer
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Compétences</h3>
          <button
            type="button"
            onClick={addCompetency}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
          >
            Ajouter une compétence
          </button>
        </div>

        <div className="space-y-4">
          {config.competencies.map((competency, competencyIndex) => (
            <div key={competency.id} className="border rounded-lg p-4 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de la compétence
                  </label>
                  <input
                    type="text"
                    value={competency.name}
                    onChange={(e) => updateCompetency(competencyIndex, 'name', e.target.value)}
                    className="input-field text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Poids (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={competency.weight}
                    onChange={(e) => updateCompetency(competencyIndex, 'weight', parseInt(e.target.value) || 0)}
                    className="input-field text-sm"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={competency.description}
                  onChange={(e) => updateCompetency(competencyIndex, 'description', e.target.value)}
                  rows={2}
                  className="input-field text-sm"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Activités
                  </label>
                  <button
                    type="button"
                    onClick={() => addActivity(competencyIndex)}
                    className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-xs"
                  >
                    Ajouter
                  </button>
                </div>

                <div className="space-y-2">
                  {competency.activities.map((activity, activityIndex) => (
                    <div key={activityIndex} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={activity}
                        onChange={(e) => updateActivity(competencyIndex, activityIndex, e.target.value)}
                        className="input-field text-sm flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeActivity(competencyIndex, activityIndex)}
                        className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeCompetency(competencyIndex)}
                  className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                >
                  Supprimer cette compétence
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EvaluationDashboardConfigurationForm;
